import _ from 'lodash';
import { AuthData } from '../../context/auth';
import { dayjs } from '../../date-util';
import {
	FieldGroupCreateRequest,
	FieldValuesMap,
	Policy2,
} from '../../types/policy';
import {
	PremiumReportResponse,
	PremiumReportUpdateRequest,
} from '../../types/premiumReports';
import {
	getDateErrors,
	getPolicyCoveragePeriod,
	isEmpty,
} from '../../util/util';
import {
	PremiumReportUpdateObject,
	UpdateFieldGroup,
} from '../fastForm/updateBuilders/premiumReportUpdateBuilder';

export function transformFieldGroupCreateReq(
	premiumReport: PremiumReportResponse,
) {
	const fieldGroups: FieldGroupCreateRequest[] = [];
	const currFieldGroupsByLocator = premiumReport.fieldGroupsByLocator;
	const currFieldValues = premiumReport.fieldValues;
	Object.keys(currFieldGroupsByLocator).forEach((locator) => {
		// get fieldName based on locator
		const fieldName = Object.keys(currFieldValues).find((key) =>
			currFieldValues[key].includes(locator),
		);
		fieldGroups.push({
			fieldName,
			fieldValues: currFieldGroupsByLocator[locator],
		});
	});
	return fieldGroups;
}

export function filterOutGroupValues(premiumReport: PremiumReportResponse) {
	const fieldGroupLocators = Object.keys(premiumReport.fieldGroupsByLocator);
	const currFieldValues = { ...premiumReport.fieldValues };
	Object.keys(currFieldValues).forEach((field) => {
		if (
			currFieldValues[field].every((val) => fieldGroupLocators.includes(val))
		) {
			delete currFieldValues[field];
		}
	});
	return currFieldValues;
}

export function getUpdateReqFromPremiumReportResponse(
	premiumReport: PremiumReportResponse,
) {
	const fieldGroupsByLocator = premiumReport.fieldGroupsByLocator;
	const fieldValues = premiumReport.fieldValues;
	const endTimestamp = premiumReport.endTimestamp;
	const invoiceDueTimestamp = premiumReport.invoiceDueTimestamp;
	const updatedFieldGroups = [] as FieldGroupCreateRequest[];
	const updatedFieldValues = {} as FieldValuesMap;
	const fieldGroupLocators = Object.keys(fieldGroupsByLocator);
	Object.keys(fieldValues).forEach((fvKey: string) => {
		const fieldName = fvKey;
		const values = fieldValues[fvKey];
		// if any of the values are a locator, we know it is a fieldGroup
		if (values.length && fieldGroupLocators.includes(values[0])) {
			values.forEach((value) => {
				updatedFieldGroups.push({
					locator: value,
					fieldName,
					fieldValues: fieldGroupsByLocator[value],
				});
			});
		} else {
			// this is a field value (not a group)
			updatedFieldValues[fieldName] = values;
		}
	});
	return {
		endTimestamp,
		fieldGroups: updatedFieldGroups,
		fieldValues: updatedFieldValues,
		invoiceDueTimestamp,
		issue: false,
	};
}

export function calculateStartDate(
	premiumReports: PremiumReportResponse[],
	policy: Policy2,
) {
	const issuedReports = premiumReports.length
		? premiumReports.filter((p) => p.state === 'issued')
		: [];
	const endDates = issuedReports.map((r) => r.endTimestamp);
	const startTimestamp = issuedReports.length
		? endDates.pop()
		: policy.originalContractStartTimestamp;
	return startTimestamp;
}

export function getPremiumReportStartDate(
	premiumReport: PremiumReportResponse,
	premiumReports: PremiumReportResponse[],
	policy: Policy2,
) {
	let startTimestamp = premiumReport.startTimestamp;
	if (premiumReport.state === 'draft') {
		const reports = premiumReports.length
			? premiumReports.filter((p) => p.locator !== premiumReport.locator)
			: [];
		startTimestamp = calculateStartDate(reports, policy);
	}
	return startTimestamp;
}

export function getNewPremiumReportEndDate(
	premiumReports: PremiumReportResponse[],
	policy: Policy2,
) {
	const issuedReports = premiumReports.length
		? premiumReports.filter((p) => p.state === 'issued')
		: [];
	let date = policy.originalContractStartTimestamp;
	if (!issuedReports.length) {
		date = policy.originalContractStartTimestamp;
	} else {
		const endDates = issuedReports.map((r) => r.endTimestamp).sort();
		date = endDates.pop();
	}
	const newDate = new Date(parseInt(date.toString(), 10));
	// add one day from start date
	const updatedDate = new Date(
		newDate.getFullYear(),
		newDate.getMonth(),
		newDate.getDate() + 1,
	).getTime();
	return updatedDate.toString();
}

export function removeEmptyFieldValuesFromRequest(
	req: PremiumReportUpdateRequest,
) {
	// since premium report update requests are not additive (the entire premium report object must be sent each time)
	// the BE will throw a 400 error if there are empty fields. This function will remove any empty field Values from the request
	if (req.fieldValues) {
		const fieldValues = req.fieldValues || {};
		Object.keys(fieldValues).forEach((key) => {
			if (_.isArray(fieldValues[key]) && !fieldValues[key].length) {
				delete fieldValues[key];
			}
		});
		req.fieldValues = fieldValues;
	}
	return req;
}

export function getTodaysDateWithTimezone(timezone: string) {
	const todaysDate = new Date();
	todaysDate.setHours(0, 0, 0, 0);
	const todaysDateWithTZ = dayjs(todaysDate).tz(timezone);
	return todaysDateWithTZ.toDate();
}

export function mergeWithUpdateFieldGroups(
	updateObject: PremiumReportUpdateObject,
	updateFieldGroups: UpdateFieldGroup[],
): PremiumReportUpdateObject {
	if (
		!updateObject ||
		!updateObject.fieldGroups ||
		!updateObject.fieldGroups.length ||
		!updateFieldGroups ||
		!updateFieldGroups.length
	)
		return updateObject;

	const result = { ...updateObject };
	let fieldGroupIndex;

	updateFieldGroups.forEach((updatedFieldGroup) => {
		fieldGroupIndex = updateObject.fieldGroups.findIndex(
			(fg) => fg && fg.locator === updatedFieldGroup.fieldGroupLocator,
		);

		if (fieldGroupIndex > -1) {
			result.fieldGroups[fieldGroupIndex].fieldValues = {
				...(result.fieldGroups[fieldGroupIndex].fieldValues || {}),
				...updatedFieldGroup.fieldValues,
			};
		} else if (!isEmpty(updatedFieldGroup)) {
			// if a non-empty updated field group not exist in field groups, then add it to field groups
			result.fieldGroups.push({
				fieldName: updatedFieldGroup.fieldName,
				fieldValues: { ...updatedFieldGroup.fieldValues },
			});
		}
	});

	return result;
}

export function mergeWithRemovedFieldGroups(
	updateObject: PremiumReportUpdateObject,
	removeFieldGroups: string[],
): PremiumReportUpdateObject {
	if (
		!updateObject ||
		!updateObject.fieldGroups ||
		!updateObject.fieldGroups.length ||
		!removeFieldGroups ||
		!removeFieldGroups.length
	)
		return updateObject;

	const result = { ...updateObject };
	let fieldGroupIndex;

	removeFieldGroups.forEach((removedLocator) => {
		fieldGroupIndex = updateObject.fieldGroups.findIndex(
			(fg) => fg && fg.locator === removedLocator,
		);

		if (fieldGroupIndex > -1) {
			result.fieldGroups[fieldGroupIndex] = undefined;
		}
	});

	return result;
}

export function removeEmptyFieldGroups(
	updateObject: PremiumReportUpdateObject,
): PremiumReportUpdateObject {
	if (
		!updateObject ||
		!updateObject.fieldGroups ||
		!updateObject.fieldGroups.length
	)
		return updateObject;

	const result = { ...updateObject };
	// filter out any empty field group
	if (
		updateObject &&
		updateObject.fieldGroups &&
		updateObject.fieldGroups.length
	) {
		result.fieldGroups = result.fieldGroups.filter((fg) => fg && !isEmpty(fg));
	}

	return result;
}

export function getReportEndDateError(
	reportEnd: Date,
	reportStartDate: Date,
	policy: Policy2,
	authData: AuthData,
) {
	const { policyEndTimestamp } = getPolicyCoveragePeriod(policy);
	let reportEndDateError = {
		isValid: true,
	};

	// reportEndDate must NOT be LATER than policy's expiration date
	if (reportEndDateError.isValid) {
		reportEndDateError = getDateErrors(
			reportEnd,
			null,
			new Date(parseInt(policyEndTimestamp, 10)),
			true,
			authData,
		);
	}

	// reportEndDate must be LATER than reportStartDate
	// Note: reportStartDate is ALWAYS equal to EITHER policy's effective date
	// OR latest premium report's reportEndDate - check calculateStartDate for more information
	if (reportEndDateError.isValid) {
		reportEndDateError = getDateErrors(
			reportEnd,
			reportStartDate,
			null,
			true,
			authData,
			true,
		);
	}

	return reportEndDateError;
}
